import type * as React from 'react';

import type { TableColumnDefinition, TableDefinitions, TableView } from '@gitbook/doc-core';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';

import {
    DefinitionTitleView,
    definitionSpecs,
    RecordValue,
    useComputeTableDefinitionColorKinds,
} from '../ColumnDefinitions';
import type { TableRecordKV } from '../types';
import { ChangeTypeButton } from '../Views/cards/ChangeTypeButton';
import { MoreOptionsButton } from '../Views/cards/MoreOptionsButton';

/**
 * Line to render a specific column value.
 */
export function RecordColumnValueLine(props: {
    /** Key of the parent Table block */
    nodeKey: string;
    /** Editor instance */
    editor: EditorContainer;
    /** Is this value editable ? */
    readOnly: boolean;
    /** Record to display in the modal */
    record: TableRecordKV;
    /** Current view in the table */
    view: TableView;
    /** Table definition */
    tableDefinition: TableDefinitions;
    /** Column definition to render */
    columnDefinition: TableColumnDefinition;
    /** Is this column hidden ? */
    isHidden: boolean;
}): React.ReactElement {
    const { nodeKey, editor, readOnly, record, view, columnDefinition, tableDefinition, isHidden } =
        props;
    const definitionColorKinds = useComputeTableDefinitionColorKinds(tableDefinition, view.columns);
    return (
        <div className="group relative mb-3 flex flex-1 flex-col gap-0.5">
            <div className="mb-1 flex items-center">
                <DefinitionTitleView
                    editor={editor}
                    nodeKey={nodeKey}
                    columnDef={columnDefinition}
                    readOnly={readOnly}
                    displayMode="modal"
                />
            </div>
            <div className="flex items-start gap-2">
                {readOnly ? null : (
                    <ChangeTypeButton
                        nodeKey={nodeKey}
                        editor={editor}
                        columnDefinition={columnDefinition}
                    />
                )}
                <div className="flex min-h-8 flex-1 rounded-md border border-base px-1 py-1 pl-2">
                    <RecordValue
                        nodeKey={nodeKey}
                        editor={editor}
                        readOnly={readOnly}
                        record={record}
                        columnDefinition={columnDefinition}
                        definitionColorKind={definitionColorKinds[columnDefinition.id]}
                        selected={false}
                        displayMode="modal"
                        view={view}
                    />
                </div>
                {readOnly ? null : (
                    <MoreOptionsButton
                        nodeKey={nodeKey}
                        editor={editor}
                        view={view}
                        columnDefinition={columnDefinition}
                        leadingIcon={definitionSpecs[columnDefinition.type].icon}
                        trailingIcon={Icon.ChevronDown}
                        tableDefinition={tableDefinition}
                        isHidden={isHidden}
                    />
                )}
            </div>
        </div>
    );
}
